import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";

const tfwaCannes: ChauffeurPage = {
  slug: "tfwa-cannes",
  title: "Chauffeur privé TFWA Cannes – VTC affaires",
  subtitle:
    "Chauffeur privé pendant le TFWA World Exhibition à Cannes : transferts exécutifs, rendez-vous multi-stops autour du Palais et soirées de marques.",
  keywords: [
    "chauffeur tfwa cannes",
    "vtc tfwa cannes",
    "chauffeur privé tfwa world exhibition",
  ],
  hero: {
    title: "Chauffeur privé pendant le TFWA World Exhibition",
    subtitle:
      "Début octobre, le plus grand salon mondial du travel retail / duty-free investit le Palais : un chauffeur d'affaires pour des journées de rendez-vous serrées.",
    keywords: [
      "chauffeur TFWA Cannes",
      "VTC travel retail Cannes",
      "chauffeur Palais des Festivals",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant le TFWA World Exhibition à Cannes",
    badges: [
      "Transferts exécutifs",
      "Rendez-vous multi-stops",
      "Soirées de marques",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant le TFWA",
    items: [
      {
        title: "Agenda B2B dense",
        description:
          "Le salon enchaîne meetings et négociations toute la journée : un chauffeur en attente entre deux rendez-vous absorbe les imprévus d'agenda.",
      },
      {
        title: "Multi-stops marques & hôtels",
        description:
          "Stands au Palais, petits-déjeuners au Carlton, déjeuners au Martinez : nous relions vos points sans perte de temps ni stationnement.",
      },
      {
        title: "Aéroport de Nice pour cadres",
        description:
          "Arrivées et départs d'équipes internationales : suivi de vol, accueil et transfert direct vers votre hôtel cannois.",
      },
      {
        title: "Soirées & dîners pro",
        description:
          "Cocktails de marques, dîners clients et soirées sur la Croisette : retour tardif avec un chauffeur qui patiente.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant le TFWA Cannes",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison Nice ↔ Cannes avec suivi de vol pour les arrivées et départs des délégations travel retail.",
      },
      {
        title: "Mise à disposition exécutive",
        text: "Chauffeur dédié à la demi-journée ou à la journée pour enchaîner rendez-vous au Palais et en ville.",
      },
      {
        title: "Tournées multi-stops",
        text: "Itinéraires optimisés entre stands, hôtels et restaurants pour respecter un agenda de meetings serré.",
      },
      {
        title: "Navettes équipes & roadshows",
        text: "Coordination multi-véhicules pour délégations de marques, partenaires et roadshows commerciaux.",
      },
      {
        title: "Dîners & cocktails de marques",
        text: "Réceptions, dîners clients et soirées professionnelles, avec retour de nuit assuré.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant le TFWA",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description:
          "Halls d'exposition du salon, stands de marques et espaces de conférence.",
      },
      {
        name: "La Croisette & palaces",
        description:
          "Carlton, Martinez, Majestic : suites de réception, petits-déjeuners et dîners d'affaires.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description:
          "Terminaux 1 et 2, allers-retours fréquents des cadres début octobre.",
      },
      {
        name: "Mougins & arrière-pays",
        description: "Dîners clients et tables gastronomiques hors du centre.",
      },
      {
        name: "Mandelieu / Cannes-la-Bocca",
        description: "Hôtels complémentaires et logistique des équipes.",
      },
    ],
  },
  generic: {
    heading: "Le TFWA World Exhibition vu d'un chauffeur",
    subheading:
      "Début octobre, le travel retail mondial se donne rendez-vous au Palais — un salon B2B où chaque créneau de meeting compte.",
    paragraphs: [
      "Chaque début octobre, le TFWA World Exhibition & Congress réunit à Cannes l'industrie mondiale du duty-free et du travel retail. Au Palais des Festivals, les journées sont une succession de rendez-vous, de négociations et de présentations de marques. Un chauffeur en attente entre deux meetings vous fait gagner les minutes qui font la différence sur un agenda B2B dense.",
      "Les délégations arrivent et repartent par l'aéroport de Nice Côte d'Azur sur quelques jours. Suivi de vol, accueil et transfert direct vers l'hôtel pour absorber les pics et démarrer le salon sans attente.",
      "Le salon ne s'arrête pas aux halls : petits-déjeuners au Carlton, déjeuners au Martinez, cocktails de marques et dîners clients rythment la Croisette. Les tournées multi-stops et la mise à disposition gardent le même chauffeur, avec les retours de nuit après les soirées professionnelles.",
    ],
    bullets: [
      "Rendez-vous multi-stops",
      "Suivi de vol début octobre",
      "Retours après les cocktails",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour le TFWA Cannes",
    steps: [
      "Indiquez vos dates, votre hôtel et votre planning de rendez-vous au Palais et en ville.",
      "Nous construisons les tournées multi-stops et les liaisons aéroport selon votre agenda.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Mise à disposition journée et soirée pour meetings, déjeuners et cocktails de marques.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur pendant le TFWA Cannes ?",
      a: "Réservez en amont : début octobre, le salon mobilise Cannes et les hôtels affichent complet. Transmettez vos dates, votre hôtel et votre planning de rendez-vous pour que nous bloquions un chauffeur dédié sur toute la durée du TFWA.",
    },
    {
      q: "Pouvez-vous gérer un agenda de rendez-vous multi-stops ?",
      a: "Oui. Nous construisons des tournées optimisées entre le Palais, les suites de réception des palaces et les restaurants, avec un chauffeur en attente entre deux meetings pour absorber les décalages d'horaire.",
    },
    {
      q: "Assurez-vous les transferts aéroport des équipes internationales ?",
      a: "Oui, avec suivi de vol et accueil. Nous coordonnons les arrivées et départs des délégations travel retail entre l'aéroport de Nice et votre hôtel cannois, y compris en navettes multi-véhicules.",
    },
    {
      q: "Couvrez-vous les dîners clients et cocktails de marques le soir ?",
      a: "Oui. Les soirées professionnelles, dîners clients et cocktails de marques se terminent tard sur la Croisette ou dans l'arrière-pays : le chauffeur patiente et garantit votre retour de nuit.",
    },
  ],
  relatedLinks: [
    {
      label: "Transfert Cannes ⇄ Aéroport de Nice",
      href: "/chauffeur/transferts/cannes-aeroport-nice",
    },
    {
      label: "Transfert Cannes ⇄ Monaco",
      href: "/chauffeur/transferts/cannes-monaco",
    },
    {
      label: "Réserver un chauffeur",
      href: "/reservation",
    },
  ],
  cta: {
    title: "Votre chauffeur pour le TFWA Cannes",
    text: "Transferts exécutifs, tournées multi-stops et soirées de marques : réservez un chauffeur dédié pour le salon.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title:
      "Chauffeur TFWA Cannes — VTC affaires travel retail | KS Rent Services",
    description:
      "Chauffeur privé pendant le TFWA à Cannes : transferts exécutifs, tournées multi-stops autour du Palais, runs aéroport de Nice et soirées de marques.",
    canonical: "/chauffeur/tfwa-cannes",
  },
};

export default tfwaCannes;
